import { useState, useEffect, useMemo } from "react";
import { Box, Button, Stepper, Step, StepLabel, LinearProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import enrollmentSchema from "./validation/enrollmentSchema";
import StudentInformation from "./steps/StudentInformation";
import PriorSchool from "./steps/PriorSchool";
import HealthInsurance from "./steps/HealthInsurance";
import SpecialPrograms from "./steps/SpecialPrograms";
import Languages from "./steps/Languages";
import RecordsRelease from "./steps/RecordsRelease";
import { useDispatch, useSelector } from "react-redux";
import { getEnrollmentData, updateEnrollmentData } from "store/application/student-enrollment";
import EnrollmentDocumentUpload from "./steps/EnrollmentDocumentUpload";
import EnrollmentIntro from "./EnrollmentIntro";
import StudentProfileCard from "./StudentProfileCard";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const steps = [
  "Student Info",
  "Prior School",
  "Health Insurance",
  "Special Programs",
  "Languages",
  "Records Release",
  "Upload Documents",
];

const stepFields = {
  0: [
    "gender",
    "race",
    "ethnicity",
    "militaryStatus",
    "countryOfBirth",
    "stateOfBirth",
    "livesWith",
    "streetAddress",
    "city",
    "state",
    "zipCode",
    "parent1.firstName",
    "parent1.lastName",
    "parent1.phoneType",
    "parent1.phoneNumber",
    "parent1.email",
  ],
  1: [
    "schoolName",
    "district",
    "schoolAddress",
    "schoolPhone",
    "usSchoolEntryDate",
  ],
  2: [
    "hasInsurance",
    "insuranceProvider",
  ],
  3: [
    "giftedProgram",
    "specialEducation",
  ],
  4: [
    "languagesAtHome",
    "surveryQuestion1",
    "surveryQuestion2",
    "surveryQuestion3",
    "surveryQuestion4",
  ],
  5: [
    "recordsRelease",
    "releaseOfInformation",
  ],
  6: [],
};

const isValidOpaqueId = (id) => !!id && /^[A-Za-z0-9+/=_-]+$/.test(id);

const yesNoFromNullableBool = (v) => v === true ? "yes" : v === false ? "no" : "";
const nullableBoolFromYesNo = (v) => v === "yes" ? true : v === "no" ? false : null;
const toLanguageArray = (text) => !text ? [] : text.split(",").map(x => x.trim()).filter(Boolean);

const parseLanguageSurvey = (raw) => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw;
};

const mapEnrollmentToFormValues = (enrollment) => {
  const student = enrollment?.applicationSchool?.applicationStudent;
  const addr = student?.address;
  const languageSurvey = parseLanguageSurvey(enrollment?.languageSurvey);

  return {
    firstName: student?.firstName ?? "",
    middleName: student?.middleName ?? "",
    lastName: student?.lastName ?? "",
    suffix: student?.suffix ?? "",
    dateOfBirth: student?.dateOfBirth ?? "",
    gradeApplying: student?.gradeApp ?? "",
    gender: enrollment?.gender ?? "",
    ethnicity: enrollment?.ethnicity ?? "",
    militaryStatus: enrollment?.militaryStatus ?? null,
    countryOfBirth: enrollment?.countryOfBirth ?? "",
    stateOfBirth: enrollment?.stateOfBirth ?? "",
    cityOfBirth: enrollment?.cityOfBirth ?? "",
    livesWith: enrollment?.livesWith ?? "",
    race: enrollment?.raceTypeIds ?? [],
    streetAddress: addr?.street ?? "",
    addressLine2: addr?.line2 ?? "",
    city: addr?.city ?? "",
    state: addr?.state ?? "",
    zipCode: addr?.zip ?? "",
    parent1: {
      firstName: student?.parentFirstName ?? "",
      lastName: student?.parentLastName ?? "",
      phoneNumber: student?.parentPhone ?? "",
      phoneType: student?.type ?? "",
      email: student?.parentEmail ?? "",
      sameAddressAsStudent: true,
      address: {
        street: addr?.street ?? "",
        line2: addr?.line2 ?? "",
        city: addr?.city ?? "",
        state: addr?.state ?? "",
        zip: addr?.zip ?? "",
      },
    },
    schoolName: enrollment?.previousSchool ?? "",
    district: enrollment?.previousSchoolDistrict ?? "",
    schoolAddress: enrollment?.previousSchoolAddress ?? "",
    schoolPhone: enrollment?.previousSchoolPhone ?? "",
    usSchoolEntryDate: enrollment?.usSchoolEntryDate ?? "",
    hasInsurance: yesNoFromNullableBool(enrollment?.medicalInsurance),
    insuranceProvider: enrollment?.medicalInsuranceProvider ?? "",
    giftedProgram: yesNoFromNullableBool(enrollment?.giftedStudent),
    specialEducation: yesNoFromNullableBool(enrollment?.specialStudent),
    languagesAtHome: Array.isArray(languageSurvey?.question1) ? languageSurvey.question1.join(", ") : "",
    surveryQuestion1: yesNoFromNullableBool(languageSurvey?.question2),
    surveryQuestion2: yesNoFromNullableBool(languageSurvey?.question3),
    surveryQuestion3: yesNoFromNullableBool(languageSurvey?.question4),
    surveryQuestion4: yesNoFromNullableBool(languageSurvey?.question5),
    recordsRelease: enrollment?.recordsRelease ?? "",
    releaseOfInformation: enrollment?.releaseOfInformation ?? "",
  };
};

const buildPayload = (data) => {
  const usSchoolEntryDate = data.usSchoolEntryDate && dayjs(data.usSchoolEntryDate).isValid()
    ? dayjs(data.usSchoolEntryDate).format("YYYY-MM-DD")
    : null;

  return {
    gender: data.gender || null,
    raceTypeIds: data.race || [],
    ethnicity: data.ethnicity || null,
    militaryStatus: data.militaryStatus ?? null,
    countryOfBirth: data.countryOfBirth || null,
    stateOfBirth: data.stateOfBirth || "",
    cityOfBirth: String(data?.cityOfBirth) || "",
    livesWith: data.livesWith || null,
    previousSchool: data.schoolName || null,
    previousSchoolDistrict: data.district || null,
    previousSchoolAddress: data.schoolAddress || null,
    previousSchoolPhone: data.schoolPhone || null,
    usSchoolEntryDate,
    medicalInsurance: nullableBoolFromYesNo(data.hasInsurance),
    medicalInsuranceProvider: data.insuranceProvider || null,
    giftedStudent: nullableBoolFromYesNo(data.giftedProgram),
    specialStudent: nullableBoolFromYesNo(data.specialEducation),
    languageSurvey: {
      question1: toLanguageArray(data.languagesAtHome),
      question2: nullableBoolFromYesNo(data.surveryQuestion1),
      question3: nullableBoolFromYesNo(data.surveryQuestion2),
      question4: nullableBoolFromYesNo(data.surveryQuestion3),
      question5: nullableBoolFromYesNo(data.surveryQuestion4),
    },
    releaseOfInformation: data.releaseOfInformation ?? null,
    address: {
      street: data.streetAddress || "",
      line2: data.addressLine2 || "",
      city: data.city || "",
      state: data.state || "",
      zip: data.zipCode || "",
    },
    parent: {
      parentFirstName: data.parent1?.firstName || null,
      parentLastName: data.parent1?.lastName || null,
      parentPhone: data.parent1?.phoneNumber || null,
      type: data.parent1?.phoneType || null,
      parentEmail: data.parent1?.email || null,
    },
  };
};

export default function EnrollmentPage() {
  const [page, setPage] = useState("intro");
  const [activeStep, setActiveStep] = useState(0);
  const { appSchoolId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const enrollment = useSelector((state) => state.application?.studentEnrollment?.enrollment);
  const introStudent = enrollment?.applicationSchool?.applicationStudent;

  const resolver = useMemo(
    () => yupResolver(enrollmentSchema, { context: { activeStep } }),
    [activeStep]
  );

  const methods = useForm({
    resolver,
    mode: "onTouched",
    defaultValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      suffix: "",
      dateOfBirth: "",
      gradeApplying: "",
      gender: "",
      ethnicity: "",
      race: [],
      militaryStatus: null,
      countryOfBirth: "",
      stateOfBirth: "",
      cityOfBirth: "",
      livesWith: "",
      streetAddress: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      parent1: {
        firstName: "",
        lastName: "",
        phoneNumber: "",
        phoneType: "",
        email: "",
        sameAddressAsStudent: true,
        address: { street: "", line2: "", city: "", state: "", zip: "" },
      },
      schoolName: "",
      district: "",
      schoolAddress: "",
      schoolPhone: "",
      usSchoolEntryDate: "",
      hasInsurance: "",
      insuranceProvider: "",
      giftedProgram: "",
      specialEducation: "",
      languagesAtHome: "",
      surveryQuestion1: "",
      surveryQuestion2: "",
      surveryQuestion3: "",
      surveryQuestion4: "",
      recordsRelease: "",
      releaseOfInformation: "",
    },
  });

  useEffect(() => {
    if (!isValidOpaqueId(appSchoolId)) return;
    dispatch(getEnrollmentData({ id: appSchoolId })).then((res) => {
      if (res.payload?.finalized) setActiveStep(6);
    });
  }, [appSchoolId, dispatch]);

  useEffect(() => {
    if (!enrollment) return;
    methods.reset(mapEnrollmentToFormValues(enrollment));
  }, [enrollment, methods]);

  const onSubmit = async (data) => {
    const payload = buildPayload(data);
    const res = await dispatch(updateEnrollmentData({ id: appSchoolId, ...payload }));
    if (updateEnrollmentData.rejected?.match(res)) {
      console.log("PATCH FAILED:", res);
      return;
    }
    dispatch(getEnrollmentData({ id: appSchoolId }));
    setActiveStep((s) => s + 1);
  };

  const handleNext = async () => {
    const fieldsToValidate = stepFields[activeStep] || [];
    const valid = fieldsToValidate.length ? await methods.trigger(fieldsToValidate) : true;
    if (!valid) return;
    if (activeStep === steps.length - 2) {
      methods.handleSubmit(onSubmit)();
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const renderStep = () => {
    switch (activeStep) {
      case 0: return <StudentInformation />;
      case 1: return <PriorSchool />;
      case 2: return <HealthInsurance />;
      case 3: return <SpecialPrograms />;
      case 4: return <Languages />;
      case 5: return <RecordsRelease />;
      case 6: return <EnrollmentDocumentUpload enrollment={enrollment} />;
      default: return null;
    }
  };

  if (!enrollment) return <LinearProgress />;
  if (page === "intro")
    return <EnrollmentIntro introStudent={introStudent} setPage={setPage} enrollment={enrollment} />;

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 1 }}>
        <StudentProfileCard student={introStudent} />

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            backgroundColor: "background.paper",
            p: 2,
            borderRadius: 2,
            flexWrap: "wrap",
            "& .MuiStepLabel-label": { fontSize: "14px", fontWeight: 500 },
            "& .MuiSvgIcon-root": { fontSize: "35px" },
            "& .MuiStepConnector-root": { top: "20px" },
          }}
        >
          {steps.map((label, index) => (
            <Step
              key={label}
              sx={{ cursor: (enrollment?.finalized || activeStep === 6) ? "pointer" : "default" }}
              onClick={() => { if (enrollment?.finalized || activeStep === 6) setActiveStep(index); }}
            >
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box>{renderStep()}</Box>

        {activeStep !== 6 && (
          <Box
            sx={{
              display: "inline-flex",
              gap: 2,
              mt: 1,
              backgroundColor: "background.paper",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Button disabled={activeStep === 0} variant="outlined" onClick={handleBack}>
              {t("Previous Section")}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={enrollment?.finalized && activeStep === 5}
            >
              {activeStep === 5 ? t("Submit") : t("Next Section")}
            </Button>
          </Box>
        )}
      </Box>
    </FormProvider>
  );
}
